import React, { useState, useEffect } from 'react';
import { Play, CheckCircle, AlertCircle, BookOpen, Clock, Search, ShieldCheck } from 'lucide-react';
import { docDistributionService, CbtCourse, KnowledgeBaseArticle } from '../../documents/docDistributionService';

export const CbtLearningHubTab: React.FC = () => {
  const [courses, setCourses] = useState<CbtCourse[]>([]);
  const [kbArticles, setKbArticles] = useState<KnowledgeBaseArticle[]>([]);
  const [searchKb, setSearchKb] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<CbtCourse | null>(null);
  const [activeModuleIndex, setActiveModuleIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [playerSeconds, setPlayerSeconds] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cData, kData] = await Promise.all([
        docDistributionService.getCourses(),
        docDistributionService.getKnowledgeBaseArticles(searchKb || undefined),
      ]);
      setCourses(cData || []);
      setKbArticles(kData || []);
    } catch (e: any) {
      console.error('Failed to load CBT data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateWatch = async (advanceSeconds: number) => {
    if (!selectedCourse) return;
    const currentMod = selectedCourse.modules?.[activeModuleIndex];
    if (!currentMod) return;

    const newSec = playerSeconds + advanceSeconds;
    const duration = currentMod.durationSeconds || 300;
    const watchPct = Math.min(100, Math.round((newSec / duration) * 100));

    try {
      const res = await docDistributionService.submitCbtProgress({
        course_id: selectedCourse.id,
        module_id: currentMod.id,
        last_position_seconds: newSec,
        watch_percent: watchPct,
      });

      setPlayerSeconds(newSec);
      if (res.completed) {
        setMsg(`🎉 Module completed! (${watchPct}%)`);
      } else {
        setMsg(`Progress saved: ${newSec}s (${watchPct}%)`);
      }
      setTimeout(() => setMsg(null), 3000);
      await loadData();
    } catch (err: any) {
      setMsg(`Anti-Cheat alert: ${err.message || 'Suspicious seek detected'}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <BookOpen size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{courses.length}</div>
              <div className="text-xs text-slate-500 font-medium">Digital CBT Courses</div>
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
              <ShieldCheck size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">Strict Anti-Cheat</div>
              <div className="text-xs text-slate-500 font-medium">Heartbeat & Seeker Guard Active</div>
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
              <Clock size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{kbArticles.length}</div>
              <div className="text-xs text-slate-500 font-medium">Knowledge Base Articles</div>
            </div>
          </div>
        </div>
      </div>

      {msg && (
        <div className="p-3 bg-slate-900 text-white text-sm rounded-lg flex items-center justify-between">
          <span>{msg}</span>
          <button onClick={() => setMsg(null)} className="text-xs text-slate-400 hover:text-white">✕</button>
        </div>
      )}

      {/* Courses & Player Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Course Catalog */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm lg:col-span-1">
          <h3 className="font-bold text-slate-800 text-base mb-3">Available Digital Modules</h3>
          <div className="space-y-3">
            {courses.map((c) => (
              <div
                key={c.id}
                onClick={() => {
                  setSelectedCourse(c);
                  setActiveModuleIndex(0);
                  setPlayerSeconds(0);
                }}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedCourse?.id === c.id
                    ? 'border-indigo-600 bg-indigo-50/50 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex justify-between items-start">
                  <h4 className="font-semibold text-sm text-slate-800">{c.title}</h4>
                  <span className="text-xs px-2 py-0.5 bg-slate-100 rounded text-slate-600 font-medium">
                    {c.progress_percent || 0}%
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description || 'Standard recurrent syllabus'}</p>
                <div className="flex items-center gap-4 text-xs text-slate-400 mt-3 font-medium">
                  <span>{c.modules?.length || 0} modules</span>
                  <span>{Math.round((c.durationMinutes || 30))} mins</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Video Player Simulator */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm lg:col-span-2">
          {selectedCourse ? (
            <div>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-bold text-lg text-slate-800">{selectedCourse.title}</h3>
                  <span className="text-xs text-slate-500 font-medium">
                    Module {activeModuleIndex + 1} of {selectedCourse.modules?.length || 1}: {selectedCourse.modules?.[activeModuleIndex]?.title || 'Module 1'}
                  </span>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
                  Anti-Cheat Protection Active
                </span>
              </div>

              {/* Player Screen Mock */}
              <div className="bg-slate-900 rounded-xl aspect-video flex flex-col items-center justify-center text-white relative overflow-hidden">
                <Play size={48} className="text-white/80 mb-2 cursor-pointer hover:scale-110 transition-transform" />
                <span className="text-sm font-semibold text-slate-300">
                  {selectedCourse.modules?.[activeModuleIndex]?.title || selectedCourse.title}
                </span>
                <span className="text-xs text-slate-400 mt-1">
                  Position: {playerSeconds}s / {selectedCourse.modules?.[activeModuleIndex]?.durationSeconds || 300}s
                </span>

                {/* Progress bar */}
                <div className="absolute bottom-0 left-0 right-0 h-2 bg-slate-800">
                  <div
                    className="h-full bg-indigo-500 transition-all duration-300"
                    style={{
                      width: `${Math.min(100, Math.round((playerSeconds / (selectedCourse.modules?.[activeModuleIndex]?.durationSeconds || 300)) * 100))}%`
                    }}
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSimulateWatch(10)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm"
                  >
                    ▶ Watch 10s (Normal Play)
                  </button>
                  <button
                    onClick={() => handleSimulateWatch(30)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg border border-slate-300"
                    title="Tests anti-cheat threshold"
                  >
                    ⏩ Fast-Forward 30s (Triggers Anti-Cheat)
                  </button>
                </div>
                <div className="text-xs text-slate-500">
                  Automatic completion threshold: 95%
                </div>
              </div>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-slate-400">
              <Play size={36} className="mb-2 text-slate-300" />
              <p className="text-sm font-medium">Select a CBT course from the left to start learning session</p>
            </div>
          )}
        </div>
      </div>

      {/* Knowledge Base Section */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-bold text-base text-slate-800">Operational Knowledge Base & SOP Search</h3>
            <p className="text-xs text-slate-500">Searchable aviation manuals, bulletins, and quick reference handbooks</p>
          </div>
          <div className="relative w-64">
            <Search size={14} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search SOPs or articles..."
              value={searchKb}
              onChange={(e) => {
                setSearchKb(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadData();
              }}
              className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kbArticles.map((art) => (
            <div key={art.id} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-all bg-slate-50/50">
              <div className="flex justify-between items-start">
                <span className="text-xs font-semibold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                  {art.category}
                </span>
                <span className="text-xs text-slate-400">v{art.version || '1.0'}</span>
              </div>
              <h4 className="font-semibold text-sm text-slate-800 mt-2">{art.title}</h4>
              <p className="text-xs text-slate-600 mt-1 line-clamp-3">{art.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
